<!-- NOTICE: KAPTION-INTEGRATION -->
<!-- Dropdown showing individual dashboard app notifications with custom actions -->
<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import DashboardAppIcon from './DashboardAppIcon.vue';
import { useMapGetter } from 'dashboard/composables/store';

const emit = defineEmits(['selectApp', 'clearAll', 'actionClick']);
const store = useStore();
const { t } = useI18n();

// Get all notifications and dashboard apps
const notificationsList = useMapGetter(
  'dashboardAppBadges/getNotificationsList'
);
const allDashboardApps = useMapGetter('dashboardApps/getRecords');

// Get sidebar apps only
const sidebarApps = computed(() =>
  allDashboardApps.value.filter(app => app.placement === 'sidebar')
);

// Create a map of app ID to app for quick lookup
const appMap = computed(() => {
  const map = {};
  sidebarApps.value.forEach(app => {
    map[app.id] = app;
  });
  return map;
});

// Enrich notifications with app data
const enrichedNotifications = computed(() => {
  return notificationsList.value
    .map(notification => {
      const app = appMap.value[notification.appId];
      if (!app) return null; // Skip notifications for unknown apps

      return {
        ...notification,
        app,
        appTitle: app.sidebar_settings?.label || app.title,
        appIcon: app.icon,
      };
    })
    .filter(Boolean);
});

const hasNotifications = computed(() => enrichedNotifications.value.length > 0);

// Click handlers
const handleNotificationClick = notification => {
  // Clicking the notification body opens the app
  emit('selectApp', notification.app);
};

const handleActionClick = (notification, action, event) => {
  event.stopPropagation();

  // Emit action click event for parent to handle
  emit('actionClick', {
    notificationId: notification.id,
    appId: notification.appId,
    action,
  });

  // NOTICE: KAPTION-INTEGRATION - Send the action to the app via postMessage
  // The iframe stays alive when sidebar is closed (v-show instead of v-if)
  const frameId = `dashboard-app--frame-sidebar-${notification.appId}-0`;
  const frameElement = document.getElementById(frameId);

  if (frameElement?.contentWindow) {
    const message = {
      type: 'chatwoot-dashboard-notification-action',
      data: {
        notificationId: notification.id,
        actionId: action.id,
        action,
      },
    };
    frameElement.contentWindow.postMessage(JSON.stringify(message), '*');
  }
};

const handleClearAll = () => {
  store.dispatch('dashboardAppBadges/clearAllNotifications');
  emit('clearAll');
};

const handleDismiss = (notification, event) => {
  event.stopPropagation();
  store.dispatch('dashboardAppBadges/dismissNotification', {
    notificationId: notification.id,
  });
};

// Get button variant class
const getActionVariant = variant => {
  switch (variant) {
    case 'primary':
      return 'bg-n-blue-9 text-white hover:bg-n-blue-10';
    case 'danger':
      return 'bg-n-ruby-9 text-white hover:bg-n-ruby-10';
    case 'success':
      return 'bg-n-green-9 text-white hover:bg-n-green-10';
    case 'warning':
      return 'bg-n-amber-9 text-n-slate-12 hover:bg-n-amber-10';
    default:
      return 'bg-n-alpha-3 text-n-slate-12 hover:bg-n-alpha-4';
  }
};
</script>

<template>
  <div
    class="notification-dropdown bg-n-solid-2 border border-n-weak rounded-lg shadow-xl min-w-[300px] max-w-[360px] overflow-hidden"
  >
    <!-- Header -->
    <div
      class="px-3 py-2 border-b border-n-weak flex items-center justify-between"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('INTEGRATION_SETTINGS.DASHBOARD_APPS.NOTIFICATIONS.TITLE') }}
      </span>
      <button
        v-if="hasNotifications"
        class="text-xs text-n-blue-text hover:text-n-blue-text-hover transition-colors"
        @click="handleClearAll"
      >
        {{ t('INTEGRATION_SETTINGS.DASHBOARD_APPS.NOTIFICATIONS.CLEAR_ALL') }}
      </button>
    </div>

    <!-- Notification list (individual, scrollable) -->
    <div
      v-if="hasNotifications"
      class="max-h-[320px] overflow-y-auto divide-y divide-n-weak"
    >
      <div
        v-for="notification in enrichedNotifications"
        :key="notification.id"
        class="notification-item"
      >
        <!-- Main notification area (clickable to open app) -->
        <button
          class="w-full px-3 py-2.5 flex items-start gap-3 hover:bg-n-alpha-1 transition-colors text-left"
          @click="handleNotificationClick(notification)"
        >
          <!-- App icon -->
          <div
            class="flex-shrink-0 w-8 h-8 rounded-full bg-n-alpha-2 flex items-center justify-center"
          >
            <DashboardAppIcon
              :icon="notification.appIcon"
              :title="notification.appTitle"
              :size="16"
            />
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <!-- App name -->
            <span
              class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ notification.appTitle }}
            </span>

            <!-- Message -->
            <p class="text-sm text-n-slate-12 mt-0.5 line-clamp-2">
              {{ notification.message }}
            </p>
          </div>

          <!-- Dismiss button -->
          <button
            class="flex-shrink-0 p-1 text-n-slate-10 hover:text-n-slate-12 transition-colors rounded hover:bg-n-alpha-2"
            :title="
              t('INTEGRATION_SETTINGS.DASHBOARD_APPS.NOTIFICATIONS.CLEAR')
            "
            @click="handleDismiss(notification, $event)"
          >
            <span class="i-lucide-x text-sm" />
          </button>
        </button>

        <!-- Action buttons (if any) -->
        <div
          v-if="notification.actions && notification.actions.length > 0"
          class="px-3 pb-2.5 pt-0 flex items-center gap-2 flex-wrap"
        >
          <button
            v-for="action in notification.actions"
            :key="action.id"
            class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
            :class="getActionVariant(action.variant)"
            @click="handleActionClick(notification, action, $event)"
          >
            {{ action.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="px-4 py-6 text-center">
      <span class="i-lucide-bell-off text-2xl text-n-slate-11 mb-2 block" />
      <p class="text-sm text-n-slate-11">
        {{ t('INTEGRATION_SETTINGS.DASHBOARD_APPS.NOTIFICATIONS.EMPTY') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.notification-dropdown {
  animation: dropdown-in 0.15s ease-out;
}

@keyframes dropdown-in {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
